import { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import FlowHeader from './FlowHeader'
import FlowConnections from '../components/FlowConnections'
import MainThemeNode from '../components/MainThemeNode'
import ThemeNodes from '../components/ThemeNodes'
import TooltipOverlay from '../components/TooltipOverlay'
import { useTooltip } from '../hooks/useTooltip'
import { calculateLayout } from '../helpers/layoutHelpers'
import { createSVThemeNode, createThemeNodes, createLinks } from '../helpers/nodeHelpers'
import { createThemeTooltipData, createMainThemeTooltipData } from '../helpers/tooltipHelpers'

// Diagram component that renders the mapping between themes, social criteria, and impacts
// Shows connections between SV Theme and individual theme social criteria with curved lines
const FlowDiagram = ({ themes, formatCurrency, isExporting = false }) => {
  const [hoveredNodeId, setHoveredNodeId] = useState(null)
  const { tooltip, isVisible, mousePosition, showTooltip, hideTooltip, updateMousePosition } = useTooltip()

  // Calculate responsive layout dimensions and positions
  // Use dynamic layout when exporting to ensure all content is visible
  const layout = useMemo(() => {
    return calculateLayout(themes, 1000, isExporting, formatCurrency)
  }, [themes, isExporting, formatCurrency])

  // Generate all chart data: nodes and their connecting links
  const mainThemeNode = useMemo(() => createSVThemeNode(layout, themes), [layout, themes])
  const allThemeNodes = useMemo(
    () => createThemeNodes(themes, layout, formatCurrency),
    [themes, layout, formatCurrency]
  )
  const connectionLinks = useMemo(
    () => createLinks(themes, mainThemeNode, allThemeNodes),
    [themes, mainThemeNode, allThemeNodes]
  )

  // Handle node hover events
  const handleNodeHover = useCallback(
    (node, event) => {
      setHoveredNodeId(node.id)
      const tooltipData =
        node.id === 'left' ? createMainThemeTooltipData() : createThemeTooltipData(node, formatCurrency)

      if (tooltipData) {
        showTooltip(tooltipData, event)
      }
    },
    [formatCurrency, showTooltip]
  )

  const handleNodeLeave = useCallback(() => {
    setHoveredNodeId(null)
    hideTooltip()
  }, [hideTooltip])

  const handleNodeMove = useCallback(
    event => {
      updateMousePosition(event)
    },
    [updateMousePosition]
  )

  return (
    <>
      <svg
        viewBox={`0 0 ${layout.baseWidth} ${layout.baseHeight}`}
        width="100%"
        height="100%"
        style={{ maxWidth: '100%', height: 'auto' }}
      >
        {/* Column headers (SV Theme, Social Criteria, Impact) */}
        <FlowHeader layout={layout} />

        {/* Curved connection lines between all nodes */}
        <FlowConnections links={connectionLinks} />

        {/* Central SV Theme node (black pill with label) */}
        <MainThemeNode
          node={mainThemeNode}
          isHovered={hoveredNodeId === 'left'}
          onNodeHover={handleNodeHover}
          onNodeLeave={handleNodeLeave}
          onNodeMove={handleNodeMove}
        />

        {/* All theme, social criteria, and impact nodes (colored pills) */}
        <ThemeNodes
          nodes={allThemeNodes}
          hoveredNodeId={hoveredNodeId}
          onNodeHover={handleNodeHover}
          onNodeLeave={handleNodeLeave}
          onNodeMove={handleNodeMove}
          isExporting={isExporting}
        />
      </svg>

      {/* Tooltip overlay with CSS animations */}
      <TooltipOverlay tooltip={tooltip} isVisible={isVisible} mousePosition={mousePosition} />
    </>
  )
}

FlowDiagram.propTypes = {
  themes: PropTypes.arrayOf(
    PropTypes.shape({
      theme_name: PropTypes.string.isRequired,
      social_criteria: PropTypes.string,
      reporting_metrics: PropTypes.arrayOf(PropTypes.string),
      total_impact: PropTypes.number.isRequired,
      stakeholder_count: PropTypes.number.isRequired
    })
  ).isRequired,
  formatCurrency: PropTypes.func.isRequired,
  isExporting: PropTypes.bool
}

export default FlowDiagram
